import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import clone from 'ramda/src/clone';
import drop from 'ramda/src/drop';
import find from 'ramda/src/find';
import indexOf from 'ramda/src/indexOf';
import join from 'ramda/src/join';
import propEq from 'ramda/src/propEq';
import split from 'ramda/src/split';
import Accordion from '@components/@core/Accordion';
import Filter from '@components/@core/Filter';
import Input from '@components/@core/Input';
import Thumbnail from '@components/@core/Thumbnail';
import Toggle from '@components/@core/Toggle';
import EditDropdown from '@components/EditDropdown';
import { CardType, LibraryCardDetailMenu } from '~/services/config';
import { getSettings } from '~/services/util';

const Detail = ({ data, onCardMenu }) => {
    const role = useSelector((state) => state.app.app_role_id);
    const authors = useSelector((state) => state.app.authors);
    const departments = useSelector((state) => state.app.departments);
    const competencies = useSelector((state) => state.app.competencies);
    const categories = useSelector((state) => state.app.categories);

    const TYPE = find(propEq('id', data.card_type_id), CardType);
    const managerApproval = data.data.action_approval === 'manager';

    const handleDocumentName = (document) => {
        let filename = document?.split('%2F');
        if (filename.length > 1) {
            filename.splice(0, 2);
            return filename;
        } else {
            return document;
        }
    };

    const attachments = useMemo(() => {
        let temp = clone(data.data.attachments);
        if (data.data.presentation_url) temp.push(data.data.presentation_url);
        temp = temp.map((item) => {
            const idx = indexOf('attachments', item);
            const link = drop(idx, item);
            if (indexOf('%2F', link) > -1) return split('%2F', link)[2];
            else return split('/', link)[2];
        });
        return temp;
    }, [data.data]);

    const attachment = join(', ', attachments) || 'N/A';

    return (
        <>
            <Filter back="all modules" />
            <div className="library-module-card border-5">
                <div className="detail-content px-3 px-sm-0">
                    <div className="d-flex jc-between">
                        <div>
                            <p className="dsl-b22 bold mb-1">{data?.data?.en?.name || 'N/A'}</p>
                            {data?.data?.es?.name && <p className="dsl-b16 text-400 mb-0">{data.data.es.name}</p>}
                        </div>
                        <EditDropdown options={LibraryCardDetailMenu[role]} onChange={onCardMenu} />
                    </div>
                    <div>
                        <p className="dsl-m12 text-400">Module Type</p>
                        <p className="dsl-b16">{TYPE?.label || 'N/A'}</p>
                    </div>
                    <div className="d-flex ssm-column mb-4">
                        <div>
                            <p className="dsl-m12 text-400">Module Image</p>
                            <Thumbnail source={data.data.thumb_url} size="medium" />
                        </div>
                        <div className="pl-4 ssp-l-0 pt-2 mt-4 ssp-t-0 overflow-auto">
                            <p className="dsl-m12">Description</p>
                            <div className="dsl-b16" dangerouslySetInnerHTML={{ __html: data?.data?.en?.description || '' }} />
                            {data?.data?.es?.description && (
                                <>
                                    <p className="dsl-m12 mt-4">Description (Spanish)</p>
                                    <div className="dsl-b16" dangerouslySetInnerHTML={{ __html: data.data.es.description }} />
                                </>
                            )}
                        </div>
                    </div>
                    <Row className="mt-3">
                        <Col sm={6}>
                            <div className="d-flex-1">
                                <p className="dsl-m12 text-400">File</p>
                                <p className="dsl-b16 word-break">{attachment}</p>
                            </div>
                        </Col>
                        {TYPE?.label !== 'Scorm' && (
                            <Col sm={6}>
                                <div className="d-flex-1">
                                    <p className="dsl-m12 text-400">Video File</p>
                                    <p className="dsl-b16 word-break">{handleDocumentName(data?.data?.video_url) || 'N/A'}</p>
                                </div>
                            </Col>
                        )}
                        <Col sm={6}>
                            <div className="d-flex-1">
                                <p className="dsl-m12 text-400">{TYPE.label} URL</p>
                                <p className="dsl-b16 word-break">{data?.data?.video_url || 'N/A'}</p>
                            </div>
                        </Col>
                        <Col sm={6}>
                            <p className="dsl-m12">Time To Complete</p>
                            <p className="dsl-b16 ms-2">{data.data.estimated_completion} days</p>
                        </Col>
                        <Col sm={6}>
                            <Toggle checked={managerApproval} title="Manager Approval" leftLabel="Off" rightLabel="On" disabled />
                        </Col>
                    </Row>
                    {data.data?.objectives?.length > 0 && (
                        <>
                            <p className="dsl-m12 mt-4">Learning objectives:</p>
                            {data.data.objectives.map((item, index) => (
                                <div className="d-flex mt-1" key={index}>
                                    <div className="circle">{index + 1}</div>
                                    <span className="dsl-b14 ps-2 m-0 w-100 pt-1">{item}</span>
                                </div>
                            ))}
                        </>
                    )}
                    <div className="mt-4">
                        <p className="dsl-m12 text-400">Completion Points</p>
                        <p className="dsl-b16">{data.data?.completion_points || data.data?.completion_points || 'N/A'}</p>
                    </div>
                    <Accordion className="settings mt-3" icon={null}>
                        <Row className="mt-1">
                            <Col xs={12} sm={6} className="my-3">
                                <Input disabled className="input-field mb-2" title="Author" direction="vertical" value={getSettings([data.author_id], authors, 'N/A')} />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Input disabled className="input-field mb-2" title="Categories" direction="vertical" value={getSettings(data.data.category, categories, 'N/A')} />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Input disabled className="input-field mb-2" title="Competencies" direction="vertical" value={getSettings(data.data.competency, competencies, 'N/A')} />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Input disabled className="input-field mb-2" title="Departments" direction="vertical" value={getSettings(data.data.department, departments, 'N/A')} />
                            </Col>
                        </Row>
                    </Accordion>
                </div>
            </div>
        </>
    );
};

export default memo(Detail);
